<template>
  <div class="channel-selector-wrapper">
    <HR :noHrPadding="true" narrow />
    <div class="channel-selector-content">
      <AdviserSectionHeading
        :title="heading"
        :titleLight="headingLight"
        :subtitle="subtitle"
        variant="b"
        centered
      />
      <v-select
        v-model="selectedChannel"
        class="channel-selector-select"
        :items="channelOptions"
        item-text="text"
        item-value="value"
        label="Select your Old Mutual Channel"
        hint="Select from the dropdown"
        persistent-hint
        outlined
        :menu-props="{ offsetY: true, zIndex: 200 }"
      />
      <div class="channel-selector-cta">
        <GradientButton
          title="Select and go"
          :type="'button'"
          :disabled="!selectedChannel"
          @gradientButtonClicked="goToChannel"
        />
      </div>
    </div>
    <HR :noHrPadding="true" narrow />
  </div>
</template>

<script>
  import HR from '../../common/HR.vue';
  import AdviserSectionHeading from './AdviserSectionHeading.vue';

  // Channel switcher shown on /adviser (all channels), on the Resources page
  // (all channels) and on every channel page (own channel hidden) so advisers
  // land on their channel-specific registration page and resources.
  const CHANNELS = [
    { text: 'MFC', value: 'mfc', routeName: 'MfcAdviserEnrolment' },
    { text: 'IFA', value: 'ifa', routeName: 'IfaAdviserEnrolment' },
    { text: 'PFA', value: 'pfa', routeName: 'PfaAdviserEnrolment' },
    // DFA and AFD advisers enrol via the PFA channel page.
    { text: 'DFA', value: 'dfa', routeName: 'PfaAdviserEnrolment' },
    { text: 'AFD', value: 'afd', routeName: 'PfaAdviserEnrolment' },
  ];

  export default {
    name: 'AdviserChannelSelector',
    components: {
      HR,
      AdviserSectionHeading,
    },
    props: {
      // The page's own channel — excluded from the dropdown on channel pages.
      currentChannel: {
        type: String,
        default: null,
      },
      // /adviser and Resources list every channel, including the current one.
      showAllChannels: {
        type: Boolean,
        default: false,
      },
      heading: {
        type: String,
        default: 'Not the correct Channel?',
      },
      // Optional light-weight tail for the heading (Resources page).
      headingLight: {
        type: String,
        default: '',
      },
      subtitle: {
        type: String,
        default:
          'Please select your channel below. This will direct you to your channel-specific registration page and dedicated resources for your channel.',
      },
    },
    data() {
      return {
        selectedChannel: null,
      };
    },
    computed: {
      channelOptions() {
        if (this.showAllChannels) return CHANNELS;
        // Only the page's own channel is hidden. DFA/AFD stay visible on
        // /pf even though they route there — advisers searching for their
        // channel by name must always find it in the list.
        return CHANNELS.filter(c => c.value !== this.currentChannel);
      },
    },
    methods: {
      goToChannel() {
        const channel = CHANNELS.find(c => c.value === this.selectedChannel);
        if (!channel) return;
        // vue-router 3.0 aborts a push() to the current route, which makes
        // the button look broken when the picked channel already lives on
        // this page (DFA/AFD picked on /pf) — so force a full browser load.
        if (this.$route.name === channel.routeName) {
          const { href } = this.$router.resolve({ name: channel.routeName });
          window.location.assign(href);
          return;
        }
        this.$router.push({ name: channel.routeName });
      },
    },
  };
</script>

<style scoped lang="scss">
  @import '../../../constants/styles/adviser-pages.scss';

  .channel-selector-wrapper {
    position: relative;
    width: 100%;
  }

  .channel-selector-content {
    @include adviser-content-column;
    padding: 60px 0 40px;
    text-align: center;
  }

  .channel-selector-select {
    max-width: 604px;
    margin: 40px auto 0;

    ::v-deep .v-input__slot {
      background-color: #ffffff;
      min-height: 56px;
    }
    ::v-deep fieldset {
      border-radius: 16px;
      border-color: $adviser-border-field;
    }
    ::v-deep .v-input--is-focused fieldset {
      border: 2px solid $adviser-green-dark;
    }
    ::v-deep .v-messages {
      text-align: left;
      color: $adviser-text-muted;
    }
    ::v-deep .v-select__selection,
    ::v-deep .v-label {
      @include adviser-font;
      font-size: 16px;
      color: $adviser-text-black;
    }
  }

  .channel-selector-cta {
    display: flex;
    justify-content: center;
    margin-top: 40px;
  }
</style>
